/**
 * The rock scanner (spec §5.2, plan §2.9 `mining.json scan`, `modules.json` scanner stats). Hold R on a rock
 * within range for `scan.holdSeconds`; the result shows the ore mix, an estimated value, the mode and the
 * resistance with the fitted scanner's accuracy:
 * - Mk I: the value and every ore share as a range (± `valueSpreadMk1`, relative);
 * - Mk II: one estimate within ± `valueSpreadMk2` (a stable per-rock error, not re-rolled per scan);
 * - Mk III: exact.
 * Mk I-II do not identify tetherstone (the HUD shows its `unidentifiedName`, D-031). Every Mk also reads the rock's
 * field grade and the system's signature ore when the rock's field holds it (MC-a6, M8 5(a)). Allocation-free (D-014).
 */
import { TETHERSTONE_INDEX, WARE_COUNT, type MiningConfig } from '../data/m2Schemas'
import type { MiningMode, NavKind, RockScan } from '../space/contracts'
import { seededUnit } from '../space/hash'
import { rockValue, type WarePrices } from './composition'

const SALT_SCAN_VALUE = 0x5ca0
const SALT_SCAN_SHARE = 0x5cb0 // + ware index

/** What the runtime knows about a rock or fragment (caller-owned scratch, filled before `scanRock`). */
export interface RockFacts {
  /** Stable rock id (`FieldLayout.id`), or a stable fragment key: it seeds the Mk II error. */
  id: number
  /** Remaining tonnes. */
  massT: number
  /** Ore share per ware index (sums to 1). */
  mix: Float32Array
  mode: MiningMode
  resistance: number
  tier: number
  /** The field grade multiplier the rock was composed with; 0 outside every field (MC-a6). */
  grade: number
  /** The system's signature ore (ware index) when the rock's field holds it, else -1 (MC-a6). */
  signature: number
}

/** Distance from the scanner at `s` to the surface of the rock at `c` with `radius` is within `range`. */
export function scanInRange(sx: number, sy: number, sz: number, cx: number, cy: number, cz: number, radius: number, range: number): boolean {
  const dx = cx - sx
  const dy = cy - sy
  const dz = cz - sz
  const reach = range + radius
  return dx * dx + dy * dy + dz * dz <= reach * reach
}

/** Ware label for a scan: tetherstone reads as its `unidentifiedName` unless identified (Mk III). */
export function scannedWareName(
  ware: number,
  identified: boolean,
  wares: ReadonlyArray<{
    readonly name: string
    readonly unidentifiedName?: string
  }>,
): string {
  const def = wares[ware]
  return ware === TETHERSTONE_INDEX && !identified && def.unidentifiedName !== undefined ? def.unidentifiedName : def.name
}

/** Stable Mk II relative error in [−spread, +spread) for stream `salt` of rock `id`. */
function stableError(fieldSeed: number, id: number, salt: number, spread: number): number {
  return (2 * seededUnit(fieldSeed, id, salt) - 1) * spread
}

/**
 * Writes the scan of `rock` with scanner Mk `scannerMk` (clamped to 1-3) into `out`. `kind` and `index` are the
 * nav target the scan belongs to. The range check is the caller's (`scanInRange` with the fitted scanner's range).
 */
export function scanRock(
  rock: RockFacts,
  fieldSeed: number,
  kind: NavKind,
  index: number,
  scannerMk: number,
  cfg: Pick<MiningConfig, 'scan'>,
  wares: WarePrices,
  out: RockScan,
): RockScan {
  const mk = scannerMk <= 1 ? 1 : scannerMk >= 3 ? 3 : Math.round(scannerMk)
  const value = rockValue(rock.mix, rock.massT, wares)
  out.valid = true
  out.kind = kind
  out.index = index
  out.mk = mk
  out.massT = rock.massT
  out.mode = rock.mode
  out.resistance = rock.resistance
  out.tier = rock.tier
  out.tetherstoneIdentified = mk >= 3
  out.grade = rock.grade
  out.signature = rock.signature
  if (mk === 1) {
    const s = cfg.scan.valueSpreadMk1
    out.valueLow = value * (1 - s)
    out.valueHigh = value * (1 + s)
    for (let w = 0; w < WARE_COUNT; w++) {
      const share = rock.mix[w]
      out.shareLow[w] = share * (1 - s)
      out.shareHigh[w] = Math.min(1, share * (1 + s))
    }
  } else if (mk === 2) {
    const s = cfg.scan.valueSpreadMk2
    const v = value * (1 + stableError(fieldSeed, rock.id, SALT_SCAN_VALUE, s))
    out.valueLow = v
    out.valueHigh = v
    for (let w = 0; w < WARE_COUNT; w++) {
      const est = Math.min(1, rock.mix[w] * (1 + stableError(fieldSeed, rock.id, SALT_SCAN_SHARE + w, s)))
      out.shareLow[w] = est
      out.shareHigh[w] = est
    }
  } else {
    out.valueLow = value
    out.valueHigh = value
    for (let w = 0; w < WARE_COUNT; w++) {
      out.shareLow[w] = rock.mix[w]
      out.shareHigh[w] = rock.mix[w]
    }
  }
  return out
}
